import Konva from 'konva';
import React from 'react';
import { Group, Line, Rect, RegularPolygon, Text } from 'react-konva';

import { dimensionLines } from '../colors';
import { useKonva } from '../KonvaContext';
import { Box } from '../types';

const DIMENSION_LINE_TRIANGLE_SIZE = 28;

type LinePrimitiveProps = {
  text: string;
  direction: 'horizontal' | 'vertical';
  box: Box;
  options?: LineOptions;
};

type LineOptions = {
  color?: string;
  padding?: number;
  triangleSize?: number;
  altTriangle?: boolean;
};

export const LineCaption = ({
  text,
  direction,
  box,
  options,
}: LinePrimitiveProps) => {
  const { renderStyles, stageScaleX } = useKonva();

  // Create a relative text scale that maintains constant text size regardless of stage scale
  const { textScale, textSize, textPadding } = renderStyles.caption;
  const relativeTextScale = textScale / stageScaleX;

  const {
    color = dimensionLines.frame,
    padding = textPadding * (relativeTextScale * 1.4),
    triangleSize = DIMENSION_LINE_TRIANGLE_SIZE,
    altTriangle = false,
  } = options || {};
  const textRef = React.useRef<Konva.Text>(null);

  React.useEffect(() => {
    if (textRef.current) {
      setTextLabelWidth(textRef.current.width() * relativeTextScale);
    }
  }, [text, relativeTextScale]);
  const [textLabelWidth, setTextLabelWidth] = React.useState(0);

  const isVertical = direction === 'vertical';
  const length = isVertical ? box.height : box.width;

  // Triangle positions
  const triangle1 = {
    x: altTriangle ? triangleSize * 0.6 : triangleSize / 4,
    y: 0,
    rotation: isVertical ? (altTriangle ? 270 : 90) : altTriangle ? 270 : 90,
  };
  const triangle2 = {
    x: length - triangleSize / (altTriangle ? 2 : 4),
    y: 0,
    rotation: isVertical ? (altTriangle ? 90 : 270) : altTriangle ? 90 : 270,
  };

  const textBoxY = textSize / 2;
  let textBoxX = 0;
  if (isVertical) {
    textBoxX = box.height / 2 - textLabelWidth / 2;
  } else {
    textBoxX = box.width / 2 - textLabelWidth / 2;
  }

  // Rectangle for text background
  const rectX = textBoxX - padding * 1.5;
  const rectY = textBoxY + padding;
  const rectWidth = textLabelWidth + padding * 3; // crude estimate
  const rectHeight = textSize + padding * 2;

  return (
    <Group x={box.x} y={box.y}>
      <Line
        points={isVertical ? [0, 0, 0, length] : [0, 0, length, 0]}
        stroke={color}
        strokeWidth={2}
        listening={false}
      />
      <Group
        x={0}
        y={0}
        scaleY={isVertical ? -1 : 1}
        rotation={isVertical ? 90 : 0}
      >
        <Rect
          name="dimension-text-rectangle"
          x={rectX}
          y={rectY}
          width={rectWidth}
          height={rectHeight}
          fill={color}
          scaleY={-1}
          cornerRadius={10}
          listening={false}
        />
        <Text
          ref={textRef}
          name="dimension-text"
          x={textBoxX}
          y={0}
          offsetY={textBoxY}
          text={text}
          fontSize={textSize}
          fill="white"
          scaleY={isVertical ? relativeTextScale : -relativeTextScale}
          scaleX={relativeTextScale}
          listening={false}
        />
        <RegularPolygon
          x={triangle1.x}
          y={triangle1.y}
          sides={3}
          radius={triangleSize * 0.6}
          fill={color}
          rotation={triangle1.rotation}
          listening={false}
        />
        <RegularPolygon
          x={triangle2.x}
          y={triangle2.y}
          sides={3}
          radius={triangleSize * 0.6}
          fill={color}
          rotation={triangle2.rotation}
          listening={false}
        />
      </Group>
    </Group>
  );
};
